import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ChevronLeft, MapPin, Calendar, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhysioBookingPage = () => {
  const { packageId } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar';

  const [pkg, setPkg] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [purchaseLoaded, setPurchaseLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    preferred_date: '',
    preferred_time: '',
    region: '',
    city: '',
    street: '',
    address_details: '',
    condition_description: '',
    notes: '',
  });

  useEffect(() => {
    axios.get(`${API}/physio/packages`).then((r) => {
      const found = (r.data || []).find((p) => p.id === packageId);
      setPkg(found || null);
    }).catch((err) => console.warn('package load failed', err));

    axios.get(`${API}/physio/my-purchase`, { withCredentials: true })
      .then((r) => setPurchase(r.data?.active ? r.data : null))
      .catch(() => setPurchase(null))
      .finally(() => setPurchaseLoaded(true));
  }, [packageId]);

  const titleFor = (p) =>
    !p ? '' :
    language === 'fr' ? p.title_fr
    : language === 'en' ? p.title_en
    : p.title_ar;

  const onChange = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.preferred_date || !form.preferred_time) {
      setError(t('يرجى اختيار التاريخ والوقت', 'Please pick a date and time', 'Veuillez choisir une date et une heure'));
      return;
    }
    if (!form.region) {
      setError(t('يرجى اختيار المنطقة', 'Please select your region', 'Veuillez choisir votre région'));
      return;
    }
    if (!form.city.trim() || !form.street.trim()) {
      setError(t('يرجى إدخال المدينة والشارع', 'Please enter your city and street', 'Veuillez indiquer la ville et la rue'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/physio/bookings`, {
        package_id: packageId,
        ...form,
      }, { withCredentials: true });
      setSuccess(true);
      toast.success(t('تم إرسال طلب الحجز', 'Booking request sent', 'Demande envoyée'));
      setTimeout(() => navigate('/patient'), 2000);
    } catch (e) {
      setError(e.response?.data?.detail || t('فشل إرسال الطلب', 'Failed to send request', "Échec de l'envoi"));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 ${isRTL ? 'rtl' : 'ltr'} bg-gradient-to-b from-blue-50 to-white`} dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="p-8 max-w-md w-full text-center" data-testid="physio-booking-success">
          <CheckCircle2 size={64} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-extrabold text-[#0d2c54] mb-2">
            {t('تم استلام معلوماتك', 'Information Received', 'Informations reçues')}
          </h2>
          <p className="text-gray-600">
            {t('سيتواصل معك فريقنا خارجيًا لتنسيق موعد الجلسة.',
              'Our team will contact you outside the app to arrange your session.',
              "Notre équipe vous contactera hors de l'application pour organiser la séance.")}
          </p>
        </Card>
      </div>
    );
  }

  // Purchase gate — user must have paid for this package before filling the form.
  if (purchaseLoaded && (!purchase || purchase.package_id !== packageId)) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 ${isRTL ? 'rtl' : 'ltr'} bg-gradient-to-b from-blue-50 to-white`} dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="p-8 max-w-md w-full text-center" data-testid="physio-booking-locked">
          <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4">
            <MapPin size={26} />
          </div>
          <h2 className="text-xl font-extrabold text-[#0d2c54] mb-2">
            {t('يرجى شراء الباقة أولاً', 'Please purchase the package first', "Veuillez acheter le forfait d'abord")}
          </h2>
          <p className="text-gray-600 mb-5">
            {t('لإكمال الحجز يجب دفع الباقة أولاً.',
              'You must complete payment for this package before filling the booking info.',
              "Vous devez payer le forfait avant de remplir les informations.")}
          </p>
          <Button
            onClick={() => navigate('/physio')}
            className="bg-[#1565c0] hover:bg-[#0d47a1] w-full h-12 rounded-full font-bold"
            data-testid="go-to-packages-btn"
          >
            {t('عرض الباقات', 'View Packages', 'Voir les forfaits')}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'} data-testid="physio-booking-page">
      <header className="bg-gradient-to-r from-[#1565c0] to-[#0d47a1] text-white px-4 py-5 shadow-md">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Link to="/physio/packages" className="hover:bg-white/15 rounded-full p-2 transition" data-testid="back-packages-link">
            <ChevronLeft size={22} className={isRTL ? 'rotate-180' : ''} />
          </Link>
          <div className="flex-1">
            <p className="text-xs opacity-90">
              {t('حجز باقة علاج فيزيائي', 'Physiotherapy Booking', 'Réservation de kinésithérapie')}
            </p>
            <h1 className="text-lg md:text-xl font-extrabold leading-tight">
              {pkg ? titleFor(pkg) : '...'}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {pkg && purchase && (
          <Card className="p-4 mb-5 bg-emerald-50/70 border-emerald-200 relative overflow-hidden" data-testid="physio-booking-package-summary">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <p className="text-sm text-slate-700">
                <span className="font-bold text-[#0d2c54]">{titleFor(pkg)}</span>
              </p>
              <div className="text-right">
                <p className="text-[11px] text-emerald-700 font-semibold uppercase tracking-wider">
                  {t('جلسات متبقية', 'Sessions left', 'Séances restantes')}
                </p>
                <p className="text-2xl font-extrabold text-emerald-700 leading-none mt-0.5" data-testid="sessions-remaining">
                  {purchase.sessions_remaining}
                  <span className="text-sm font-semibold text-emerald-600/80 ml-1">/{purchase.sessions_total}</span>
                </p>
              </div>
            </div>
            <p className="text-[11px] text-emerald-800/80 mt-2">
              {t('تم الدفع بنجاح — يمكنك الآن حجز جلستك.',
                'Payment successful — you can now book your session.',
                "Paiement réussi — vous pouvez maintenant réserver.")}
            </p>
          </Card>
        )}

        {error && (
          <Alert variant="destructive" className="mb-4" data-testid="physio-booking-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="physio-booking-form">
          {/* Date + Time */}
          <Card className="p-4">
            <h3 className={`font-bold text-[#0d2c54] mb-3 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Calendar size={18} />
              {t('اختر الموعد المناسب لك', 'Pick your preferred date & time', "Choisissez votre date et heure")}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('التاريخ', 'Date', 'Date')}</Label>
                <Input type="date" value={form.preferred_date} onChange={(e) => onChange('preferred_date', e.target.value)}
                  required data-testid="preferred-date-input" className="mt-1"
                  min={new Date().toISOString().slice(0, 10)}
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><Clock size={12} />{t('الوقت', 'Time', 'Heure')}</Label>
                <Input type="time" value={form.preferred_time} onChange={(e) => onChange('preferred_time', e.target.value)}
                  required data-testid="preferred-time-input" className="mt-1"
                />
              </div>
            </div>
          </Card>

          {/* Location */}
          <Card className="p-4">
            <h3 className={`font-bold text-[#0d2c54] mb-3 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <MapPin size={18} />
              {t('عنوانك', 'Your Address', 'Votre adresse')}
            </h3>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t('المنطقة', 'Region', 'Région')}</Label>
                <div className="grid grid-cols-2 gap-3 mt-2" data-testid="region-options">
                  {[
                    { value: 'central_bekaa', ar: 'بقاع أوسط', en: 'Central Bekaa', fr: 'Bekaa central' },
                    { value: 'western_bekaa', ar: 'بقاع غربي', en: 'Western Bekaa', fr: 'Bekaa occidental' },
                  ].map((opt) => {
                    const active = form.region === opt.value;
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => onChange('region', opt.value)}
                        data-testid={`region-${opt.value}`}
                        className={`relative flex items-center justify-center gap-2 h-14 rounded-2xl border-2 font-bold text-base transition-all active:scale-[0.98]
                          ${active
                            ? 'border-[#1565c0] bg-[#e3f2fd] text-[#0d47a1] shadow-sm'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-[#1e88e5]/40 hover:bg-blue-50/40'
                          }`}
                      >
                        <MapPin size={18} className={active ? 'text-[#1565c0]' : 'text-gray-400'} />
                        <span>{t(opt.ar, opt.en, opt.fr)}</span>
                        {active && (
                          <span className="absolute top-1.5 end-2 w-5 h-5 rounded-full bg-[#1565c0] text-white flex items-center justify-center">
                            <CheckCircle2 size={14} strokeWidth={2.5} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('المدينة / البلدة', 'City / Town', 'Ville')}</Label>
                  <Input value={form.city} onChange={(e) => onChange('city', e.target.value)}
                    required data-testid="city-input" className="mt-1"
                    placeholder={t('مثال: زحلة', 'e.g. Zahle', 'ex. Zahlé')}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('الشارع', 'Street', 'Rue')}</Label>
                  <Input value={form.street} onChange={(e) => onChange('street', e.target.value)}
                    required data-testid="street-input" className="mt-1"
                    placeholder={t('اسم الشارع', 'Street name', 'Nom de la rue')}
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">{t('تفاصيل العنوان (طابق، علامة مميزة...)', 'Address details (floor, landmark...)', 'Détails (étage, repère)')}</Label>
                <Input value={form.address_details} onChange={(e) => onChange('address_details', e.target.value)}
                  data-testid="address-details-input" className="mt-1"
                  placeholder={t('اختياري', 'Optional', 'Optionnel')}
                />
              </div>
            </div>
          </Card>

          {/* Condition / injury description */}
          <Card className="p-4">
            <Label className="font-bold text-[#0d2c54]">
              {t('وصف الحالة أو الإصابة', 'Condition / Injury Description', 'Description de la condition')}
            </Label>
            <Textarea
              value={form.condition_description}
              onChange={(e) => onChange('condition_description', e.target.value)}
              rows={3}
              className="mt-2 resize-none"
              data-testid="condition-description-input"
              placeholder={t('مثال: ألم في أسفل الظهر منذ أسبوعين', 'e.g. Lower back pain for the past 2 weeks', "ex. Douleur au bas du dos depuis 2 semaines")}
            />
          </Card>

          {/* Notes */}
          <Card className="p-4">
            <Label className="font-bold text-[#0d2c54]">
              {t('ملاحظات للأخصائي (اختياري)', 'Notes for the specialist (optional)', 'Notes pour le spécialiste (optionnel)')}
            </Label>
            <Textarea
              value={form.notes}
              onChange={(e) => onChange('notes', e.target.value)}
              rows={3}
              className="mt-2 resize-none"
              data-testid="notes-input"
              placeholder={t('ما يجب أن يعرفه الأخصائي قبل الجلسة', 'Anything the specialist should know before the session', 'Informations pour le spécialiste')}
            />
          </Card>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1565c0] hover:bg-[#0d47a1] h-12 rounded-full font-bold text-base shadow-md disabled:opacity-60"
            data-testid="submit-physio-booking"
          >
            {submitting
              ? t('جاري الإرسال...', 'Sending...', 'Envoi...')
              : t('إرسال المعلومات', 'Submit Information', 'Envoyer les informations')}
          </Button>
          <p className="text-xs text-gray-500 text-center">
            {t('سيتواصل معك فريقنا خارجيًا لتنسيق موعد الجلسة.',
              'Our team will contact you outside the app to arrange your session.',
              "Notre équipe vous contactera hors de l'application pour organiser la séance.")}
          </p>
        </form>
      </main>
    </div>
  );
};

export default PhysioBookingPage;
